import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { calcProrata } from '../engine/calc2026';

export const ProrataBlock = ({
  userRent,
  detectedUnits,
  onProrataChange
}) => {
  const { t } = useTranslation();
  const [method, setMethod] = useState('unknown');
  const [otherRents, setOtherRents] = useState([]);
  const [manualUnits, setManualUnits] = useState(detectedUnits || 1);

  const handleMethodChange = (newMethod) => {
    setMethod(newMethod);
    
    if (newMethod === 'exact') {
      const initialRents = detectedUnits > 1 
        ? Array(detectedUnits - 1).fill(0)
        : [];
      setOtherRents(initialRents);
    }
    
    updateProrata(newMethod, otherRents, manualUnits);
  };

  const handleRentChange = (index, value) => {
    const newRents = [...otherRents];
    newRents[index] = parseFloat(value) || 0;
    setOtherRents(newRents);
    updateProrata('exact', newRents, manualUnits);
  };

  const handleUnitsChange = (value) => {
    const units = parseInt(value, 10) || 1;
    setManualUnits(units);
    updateProrata('estimated', otherRents, units);
  };

  const addRentField = () => {
    setOtherRents([...otherRents, 0]);
  };

  const removeRentField = (index) => {
    const newRents = otherRents.filter((_, i) => i !== index);
    setOtherRents(newRents);
    updateProrata('exact', newRents, manualUnits);
  };

  const updateProrata = (currentMethod, rents, units) => {
    let prorata;
    
    if (currentMethod === 'exact') {
      const validRents = rents.filter(r => r > 0);
      prorata = calcProrata(userRent, validRents, null);
    } else if (currentMethod === 'estimated') {
      prorata = calcProrata(userRent, null, units);
    } else {
      prorata = calcProrata(userRent, null, 1);
    }
    
    onProrataChange(prorata);
  };

  if (detectedUnits === 1) {
    return null;
  }

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 my-4">
      <h3 className="font-semibold text-gray-800 mb-3">
        {t('prorata.title', 'Répartition entre logements')}
      </h3>
      
      {detectedUnits > 1 && (
        <p className="text-sm text-gray-600 mb-4">
          {t('prorata.detected', {
            count: detectedUnits,
            defaultValue: `${detectedUnits} logements détectés à cette adresse.`
          })}
        </p>
      )}

      <div className="mb-4">
        <p className="font-medium text-gray-700 mb-2">
          {t('prorata.question', 'Connaissez-vous le loyer des autres logements de l\'immeuble?')}
        </p>
        
        <div className="flex flex-col gap-2">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name="prorataMethod"
              value="exact"
              checked={method === 'exact'}
              onChange={() => handleMethodChange('exact')}
              className="text-emerald-600"
            />
            <span>{t('prorata.yes', 'Oui, je connais les loyers')}</span>
          </label>
          
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name="prorataMethod"
              value="estimated"
              checked={method === 'estimated'}
              onChange={() => handleMethodChange('estimated')}
              className="text-emerald-600"
            />
            <span>{t('prorata.no', 'Non, estimer à parts égales')}</span>
          </label>
        </div>
      </div>

      {method === 'exact' && (
        <div className="border-t pt-4">
          <p className="text-sm text-gray-600 mb-3">
            {t('prorata.yourRent', 'Votre loyer:')} <strong>{userRent.toFixed(2)} $</strong>
          </p>
          
          {otherRents.map((rent, index) => (
            <div key={index} className="flex items-center gap-2 mb-2">
              <label className="text-sm text-gray-600 w-24">
                {t('prorata.unit', 'Logement')} {index + 2}:
              </label>
              <input
                type="number"
                value={rent || ''}
                onChange={(e) => handleRentChange(index, e.target.value)}
                className="border border-gray-300 rounded px-2 py-1 w-32"
                placeholder="0.00"
                min="0"
                step="0.01"
              />
              <span className="text-gray-500">$</span>
              <button
                type="button"
                onClick={() => removeRentField(index)}
                className="text-red-500 hover:text-red-700 text-sm"
              >
                {t('prorata.remove', 'Retirer')}
              </button>
            </div>
          ))}
          
          <button
            type="button"
            onClick={addRentField}
            className="text-emerald-600 hover:text-emerald-800 text-sm mt-2"
          >
            + {t('prorata.addUnit', 'Ajouter un logement')}
          </button>
        </div>
      )}

      {method === 'estimated' && (
        <div className="border-t pt-4">
          <div className="flex items-center gap-2 mb-3">
            <label className="text-sm text-gray-600">
              {t('prorata.totalUnits', 'Nombre total de logements:')}
            </label>
            <input
              type="number"
              value={manualUnits}
              onChange={(e) => handleUnitsChange(e.target.value)}
              className="border border-gray-300 rounded px-2 py-1 w-20"
              min="1"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default ProrataBlock;
